/**
 * P-08 批次详情（docs/03-UIUX.md §5）。
 *
 * 批次元信息 + 该批次授权码掩码表 + 一次性 CSV 导出入口。
 * 明文只存在于导出时的内存缓冲区，导出后不可再次获取（export_count > 0 即不可再导）。
 */
import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorBlock, EmptyFiltered, EmptyFirstRun, TableSkeleton } from '../../components/DataState';
import { Pager, DEFAULT_PAGE_SIZE, type PageSize } from '../../components/Pager';
import { describeError } from '../../api/client';
import { exportCodeBatchCsv, getCodeBatch, listLicenseCodes, listPlans, listProducts } from '../../api/endpoints';
import type { CodeBatch, LicenseCode, Product, Plan } from '../../api/types';
import { useAsync } from '../../lib/useAsync';
import { formatDate, formatDateTime } from '../../lib/format';
import { Icon } from '../../icons/Icon';
import { ICON_SIZE } from '../../tokens';
import { useAuth } from '../../auth/AuthContext';

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'available', label: '可用' },
  { value: 'exported', label: '已导出' },
  { value: 'redeemed', label: '已兑换' },
  { value: 'void', label: '已作废' },
] as const;

function triggerCsvDownload(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function BatchDetailPage() {
  const { id = '' } = useParams();
  const { currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';

  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const batch = useAsync(useCallback(() => getCodeBatch(id), [id]), [id]);
  const batchData: CodeBatch | null = batch.state.kind === 'ready' ? batch.state.data : null;

  const products = useAsync(useCallback(() => listProducts(), []), []);
  const productList: readonly Product[] = products.state.kind === 'ready' ? products.state.data.items : [];
  const product = productList.find((p) => p.id === batchData?.product_id) ?? null;

  const plans = useAsync(
    useCallback(
      () => (product ? listPlans(product.id) : Promise.resolve<PagedEmpty<Plan>>({ items: [], total: 0, page: 1, limit: 1, hasMore: false })),
      [product?.id],
    ),
    [product?.id],
  );
  const plan = plans.state.kind === 'ready' ? plans.state.data.items.find((p) => p.id === batchData?.plan_id) : null;

  const codes = useAsync(
    useCallback(() => listLicenseCodes({ batch_id: id, status: filterStatus || undefined, page, limit }), [id, filterStatus, page, limit]),
    [id, filterStatus, page, limit],
  );
  const codeList: readonly LicenseCode[] = codes.state.kind === 'ready' ? codes.state.data.items : [];
  const total = codes.state.kind === 'ready' ? codes.state.data.total : 0;
  const hasMore = codes.state.kind === 'ready' ? codes.state.data.hasMore : false;
  const hasActiveFilter = filterStatus !== '';

  const alreadyExported = (batchData?.export_count ?? 0) > 0;

  const handleExport = async () => {
    if (!batchData || alreadyExported || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const csv = await exportCodeBatchCsv(batchData.id);
      triggerCsvDownload(csv, `batch-${batchData.batch_no}.csv`);
      batch.reload();
    } catch (e) {
      setExportError(e instanceof Error ? e.message : '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const stats = useMemo(() => {
    if (!batchData?.stats) return null;
    return [
      { key: 'available', label: '可用', value: batchData.stats.available },
      { key: 'exported', label: '已导出', value: batchData.stats.exported },
      { key: 'redeemed', label: '已兑换', value: batchData.stats.redeemed },
      { key: 'void', label: '已作废', value: batchData.stats.void },
    ] as const;
  }, [batchData]);

  const statusLabel = (s: string): string => STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="text-muted" style={{ marginBottom: 4 }}>
            <Link to="/batches" className="link-mono">← 返回批次列表</Link>
          </p>
          <h1 className="page-head__title">批次详情</h1>
          <p className="page-head__sub">
            {batchData ? `批次 ${batchData.batch_no} 的授权码清单与一次性导出。` : '加载批次信息…'}
          </p>
        </div>
      </div>

      {batch.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(batch.state.error, '加载批次')} onRetry={batch.reload} />
        </div>
      ) : batch.state.kind === 'loading' || batchData === null ? (
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <div className="skeleton-bar" style={{ height: 120 }} />
          <div className="skeleton-bar" style={{ height: 120 }} />
        </div>
      ) : (
        <>
          <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="panel">
              <div className="panel__body">
                <h3 className="panel__section-title">批次信息</h3>
                <dl className="detail-list">
                  <dt>批次号</dt>
                  <dd><code>{batchData.batch_no}</code></dd>
                  <dt>产品</dt>
                  <dd>{product?.name ?? batchData.product_id.slice(0, 8)}</dd>
                  <dt>套餐</dt>
                  <dd>{plan?.name ?? batchData.plan_id.slice(0, 8)}</dd>
                  <dt>数量</dt>
                  <dd>{batchData.quantity}</dd>
                  <dt>备注</dt>
                  <dd>{batchData.note ?? '-'}</dd>
                  <dt>创建时间</dt>
                  <dd>{formatDateTime(batchData.created_at)}</dd>
                </dl>
              </div>
            </div>
            <div className="panel">
              <div className="panel__body">
                <h3 className="panel__section-title">导出与库存</h3>
                <dl className="detail-list">
                  <dt>导出状态</dt>
                  <dd>
                    <span className={`status-dot status-dot--${alreadyExported ? 'archived' : 'active'}`} />
                    {alreadyExported ? '已导出（不可再次获取）' : '未导出'}
                  </dd>
                  {alreadyExported && (
                    <>
                      <dt>首次导出时间</dt>
                      <dd>{batchData.first_exported_at ? formatDateTime(batchData.first_exported_at) : '-'}</dd>
                      <dt>操作者</dt>
                      <dd>{batchData.exported_by ?? '-'}</dd>
                    </>
                  )}
                </dl>
                {stats && (
                  <div className="batch-stats">
                    {stats.map((s) => (
                      <div key={s.key} className="batch-stats__item">
                        <span className="batch-stats__value">{s.value}</span>
                        <span className="batch-stats__label">{s.label}</span>
                      </div>
                    ))}
                  </div>
                )}
                {isOwner ? (
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={alreadyExported || exporting}
                    onClick={handleExport}
                    title={alreadyExported ? '该批次已导出过，明文不可再次获取' : '一次性导出 CSV'}
                    style={{ marginTop: 12 }}
                  >
                    <Icon name="download" size={ICON_SIZE.inline} />
                    {exporting ? '导出中…' : alreadyExported ? '已导出' : '导出 CSV'}
                  </button>
                ) : (
                  <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>导出为 owner 专属操作。</p>
                )}
                {exportError && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{exportError}</p>}
              </div>
            </div>
          </div>

          <div className="filter-bar" style={{ marginTop: 16 }}>
            <div className="filter-bar__group">
              <label className="filter-bar__label">状态</label>
              <select
                className="filter-bar__select"
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {codes.state.kind === 'failed' ? (
            <div className="table-frame" style={{ marginTop: 16 }}>
              <ErrorBlock message={describeError(codes.state.error, '加载授权码')} onRetry={codes.reload} />
            </div>
          ) : codes.state.kind === 'loading' ? (
            <div className="table-frame" style={{ marginTop: 16 }}>
              <table className="table"><TableSkeleton rows={10} columns={5} /></table>
            </div>
          ) : codeList.length === 0 && !hasActiveFilter ? (
            <div className="table-frame" style={{ marginTop: 16 }}>
              <EmptyFirstRun icon="scan-barcode" description="该批次暂无授权码。" />
            </div>
          ) : codeList.length === 0 && hasActiveFilter ? (
            <div className="table-frame" style={{ marginTop: 16 }}>
              <EmptyFiltered description="没有匹配的授权码。" onClearFilters={() => { setFilterStatus(''); setPage(1); }} />
            </div>
          ) : (
            <>
              <div className="table-frame" style={{ marginTop: 16 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>授权码（掩码）</th>
                      <th>状态</th>
                      <th>创建时间</th>
                      <th>兑换时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codeList.map((code) => (
                      <tr key={code.id}>
                        <td><code style={{ fontSize: 12 }}>{code.code_mask}</code></td>
                        <td>
                          <span className={`status-dot status-dot--${code.status}`} />
                          {statusLabel(code.status)}
                        </td>
                        <td>{formatDate(code.created_at)}</td>
                        <td>{code.redeemed_at ? formatDate(code.redeemed_at) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager
                page={page}
                limit={limit}
                total={total}
                hasMore={hasMore}
                onPageChange={setPage}
                onLimitChange={(l) => { setLimit(l); setPage(1); }}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

/** 空分页占位（避免 Plan 列表未就绪时类型膨胀） */
type PagedEmpty<T> = { items: T[]; total: number; page: number; limit: number; hasMore: boolean };
